// ─────────────────────────────────────────────────────────────
// TERROR — STORY MODE DATA
// The Abandoned Emperium of X'aol
// ─────────────────────────────────────────────────────────────

import { getAllGrimoireCards } from "./cards";

// Dialogue is styled as ancient Terror script —
// formal, cryptic, weight behind every word.

export const STORY_INTRO = {
  title: "The Abandoned Emperium",
  lore: `In a time before the Unstable World was Au Gratin, X'aol ruled from an Emperium carved from the fabric of Fate itself. When J'ynxi's devious scheme backfired and was sealed away, X'aol — pained by the sealing — abandoned the Emperium and was never seen again.

The Emperium still stands. Its gates still breathe. And its loyal soldiers still guard what was left behind.

You are an Aenarchist. You have heard the rumors — that deep within the Emperium lies a treasure that can reshape the Unstable World. Whether that treasure is power, truth, or something far older... you will not know until you reach it.

Brave the Emperium. Face what guards it. And when you reach the end —

J'ynxi will be waiting.`,
  worldDescription: `The Unstable World is untapped potential — gold and diamonds inside of caves, whirlpools that descend into black holes. Space and time are merely constructs here. Seeing is no longer believing.`,
};

// ─────────────────────────────────────────────────────────────
// DUNGEONS
// ─────────────────────────────────────────────────────────────

export const DUNGEONS = [
  {
    id: 1,
    name: "Outskirts of the Emperium",
    subtitle: "The Outer Veil",
    description: "The outermost ring of X'aol's abandoned Emperium. Three loyal soldiers stand watch over the gate. They have not moved since X'aol departed. They do not intend to.",
    flavor: "◆ The air here tastes of forgotten oaths. ◆",
    unlocked: true,
    opponents: ["suhnker", "zhefir", "tyrent"],
    rewardPack: "special",
    rewardCrests: 50,
    backgroundGradient: "linear-gradient(180deg, #0a0012, #1a0030, #0d001a)",
    accentColor: "#7c3aed",
  },
  // Dungeons 2–7 to be revealed as story expands
  {
    id: 2,
    name: "The Inner Halls",
    subtitle: "Coming Soon",
    description: "Beyond the outer gate lies the heart of the Emperium. What waits within is not yet known.",
    flavor: "◆ The gate is sealed. For now. ◆",
    unlocked: false,
    opponents: [],
    rewardPack: "special",
    rewardCrests: 75,
    backgroundGradient: "linear-gradient(180deg, #001020, #002040, #001520)",
    accentColor: "#1e40af",
  },
  {
    id: 3,
    name: "The Chao Vaults",
    subtitle: "Coming Soon",
    description: "Rumors speak of a vault where X'aol stored captured Chao. The very walls hum with contained power.",
    flavor: "◆ Power seeks those who seek power. ◆",
    unlocked: false,
    opponents: [],
    rewardPack: "special",
    rewardCrests: 100,
    backgroundGradient: "linear-gradient(180deg, #100010, #200030, #150020)",
    accentColor: "#9333ea",
  },
  {
    id: 7,
    name: "The Throne of J'ynxi",
    subtitle: "Final Dungeon",
    description: "The Solrend that started everything. J'ynxi awaits — sealed, but never silent. You have come too far to turn back.",
    flavor: "◆ With every victory, J'ynxi twists. With every defeat, J'ynxi turns. ◆",
    unlocked: false,
    opponents: ["jynxi"],
    rewardPack: "special",
    rewardCrests: 500,
    backgroundGradient: "linear-gradient(180deg, #1a0000, #3a0010, #1a0005)",
    accentColor: "#dc2626",
    isFinal: true,
  },
];

// ─────────────────────────────────────────────────────────────
// OPPONENTS
// ─────────────────────────────────────────────────────────────

export const OPPONENTS = {

  suhnker: {
    id: "suhnker",
    name: "S'uhnker",
    title: "Sentinel of the Outer Gate",
    dungeonId: 1,
    difficulty: "champion",
    starterDeckId: "starter_h09",
    avatarIcon: "⚔",
    accentColor: "#3b82f6",
    lore: "A being of few words and absolute conviction. S'uhnker has stood at the outer gate of the Emperium since X'aol's departure — not out of command, but out of choice. To him, the gate is sacred. You are not.",
    dialogue: {
      approach: [
        "◆ This gate has not opened in an age. It will not open for you.",
        "◆ Turn back, Aenarchist. What lies within is not yours to find.",
      ],
      onWin: [
        "◆ You are... stronger than the gate anticipated.",
        "◆ Pass, then. But know — those beyond are far less forgiving than I.",
      ],
      onLoss: [
        "◆ The gate holds. As it always has.",
        "◆ Come back when you understand what a Solrend truly costs.",
      ],
      midBattle: [
        "◆ Your Escendants fight well. They will not be enough.",
        "◆ H-09 does not fall easily, Aenarchist.",
      ],
    },
    rewardCrests: 75,
  },

  zhefir: {
    id: "zhefir",
    name: "Z'hefir",
    title: "Keeper of Shadows",
    dungeonId: 1,
    difficulty: "champion",
    starterDeckId: "starter_shadow_fox",
    avatarIcon: "◇",
    accentColor: "#6366f1",
    lore: "Z'hefir does not guard the gate. Z'hefir guards what the gate guards — the shadows between the stones, the spaces where light cannot reach. She has watched a thousand Aenarchists approach. She has watched all of them leave.",
    dialogue: {
      approach: [
        "◆ Your shadow arrived before you did. It told me everything I need to know.",
        "◆ The Phantom beasts answer to me here. Tread carefully.",
      ],
      onWin: [
        "◆ Hmm. Your shadow fights harder than you do. Interesting.",
        "◆ You have earned passage. Whether you survive what follows — that is another matter.",
      ],
      onLoss: [
        "◆ Even your shadow retreats now. Do the same.",
        "◆ The dark was never yours to command.",
      ],
      midBattle: [
        "◆ Intangible. You cannot touch what you cannot see.",
        "◆ The Shadow Fox has no interest in mercy.",
      ],
    },
    rewardCrests: 75,
  },

  tyrent: {
    id: "tyrent",
    name: "T'yrent",
    title: "The Last Flame of the Emperium",
    dungeonId: 1,
    difficulty: "lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    lore: "The strongest of the three outer sentinels. T'yrent was not assigned to the Emperium — T'yrent chose it, because nothing else in the Unstable World posed a sufficient challenge. The Dragon line bows to no one. T'yrent bows to X'aol alone.",
    dialogue: {
      approach: [
        "◆ I was beginning to think no one worthy would ever arrive. You may disappoint me yet.",
        "◆ Two of my companions have already fallen. I am not surprised. I am the last line for a reason.",
      ],
      onWin: [
        "◆ ...You may enter. Do not make me regret this.",
        "◆ The treasure within — it was always meant for someone like you. X'aol knew this. I did not believe it until now.",
      ],
      onLoss: [
        "◆ Even fire has its hierarchy, Aenarchist. Today you stand beneath it.",
        "◆ Return when the Chaos Dragon no longer frightens you.",
      ],
      midBattle: [
        "◆ Beings with more than 10 Lyt are instantly ruined. Remember that.",
        "◆ The Chaos Dragon does not lose. It simply... delays victory for its adversary.",
      ],
    },
    rewardCrests: 100,
    isBoss: true,   // final opponent of dungeon 1 — triggers dungeon clear
  },

  // J'ynxi — final boss, locked until dungeon 7
  jynxi: {
    id: "jynxi",
    name: "J'ynxi",
    title: "The Nefarious",
    dungeonId: 7,
    difficulty: "lord",
    starterDeckId: "starter_chaos_dragon",  // special deck to be built
    avatarIcon: "◆",
    accentColor: "#d4a84b",
    lore: "In a time before the Unstable World was Au Gratin, J'ynxi the Nefarious wrought this devious scheme disguised as a duel — its goal, to steal Anima from its adversary, X'aol. The malicious plot backfired and resulted in the eternal sealing of J'ynxi. A fate X'aol was pained to make reality. You stand now at the end of everything. J'ynxi has been waiting for an Aenarchist strong enough to release it. The question is — what happens after?",
    dialogue: {
      approach: [
        "◆ Oh. You actually made it. How delightful.",
        "◆ Every win you claimed. Every defeat you suffered. Every card you drew. I felt all of it.",
        "◆ You did not come here to free me. But that is what will happen anyway.",
      ],
      onWin: [
        "◆ ...Yes. This is the one. X'aol always knew.",
        "◆ You have done what no Aenarchist has done before. J'ynxi is released. And J'ynxi is... grateful.",
        "◆ The Unstable World was never stable. Now it is yours to shape.",
      ],
      onLoss: [
        "◆ Did you think this would be easy? That is precious.",
        "◆ Come back. I will be here. I have been here for quite some time.",
        "◆ With every defeat, J'ynxi turns. Do you feel it?",
      ],
      midBattle: [
        "◆ J'ynxi uses every card type. Did you prepare for that?",
        "◆ The Affinity belongs to me. It has always belonged to me.",
        "◆ With every victory, J'ynxi twists. With every defeat, J'ynxi turns.",
      ],
    },
    rewardCrests: 500,
    isFinalBoss: true,
  },
};

// ─────────────────────────────────────────────────────────────
// INNER EMPERIUM VILLAINS — the endless ladder
// Each villain gates on innerWins (total Inner Emperium victories)
// ─────────────────────────────────────────────────────────────
export const INNER_VILLAINS = [
  {
    id: "vael",
    name: "V'ael",
    title: "The One Who Was Left",
    innerWinsRequired: 0,
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    avatarIcon: "◇",
    accentColor: "#c9a84c",
    lore: "V'ael did not choose to guard the Inner Emperium. V'ael simply never left. Whatever reason brought them here has been forgotten — even by V'ael. What remains is presence, patience, and an absolute intolerance for those who enter without understanding what they're walking into.",
    dialogue: {
      approach: [
        "You opened the door.",
        "I didn't invite you.",
        "Those are different things.",
      ],
      onWin: [
        "...",
        "Deeper, then.",
        "Don't say I didn't warn you.",
      ],
      onLoss: [
        "The door remains closed to those who don't belong here.",
        "Come back when you've earned it.",
      ],
    },
    rewardCrests: 120,
  },

  {
    id: "sovrn",
    name: "Sov'rn",
    title: "The Archivist",
    innerWinsRequired: 3,
    difficulty: "Champion",
    starterDeckId: "starter_z4z",
    avatarIcon: "⏳",
    accentColor: "#22d3ee",
    lore: "Sov'rn has catalogued every Aenarchist who reached this corridor. The records go back further than the Plaza itself. At some point, Sov'rn stopped noting whether they survived — only whether they were interesting. The distinction matters less than it used to.",
    dialogue: {
      approach: [
        "I've recorded every Aenarchist who reached this corridor.",
        "Most of them are in the past tense now.",
        "Let's see which column you belong in.",
      ],
      onWin: [
        "Interesting.",
        "You'll get your own entry.",
        "It will be a long one.",
      ],
      onLoss: [
        "Another entry. Another past tense.",
        "The archive grows.",
      ],
    },
    rewardCrests: 150,
  },

  {
    id: "ruk",
    name: "Rük",
    title: "The Hunger",
    innerWinsRequired: 7,
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "🌑",
    accentColor: "#dc2626",
    lore: "No one knows where Rük came from. No one has asked. The Chaos Dragon that accompanies it arrived with it, and neither has explained themselves. Rük does not negotiate. Rük does not taunt. Rük simply arrives, and the field changes.",
    dialogue: {
      approach: [
        "...",
      ],
      onWin: [
        "...",
      ],
      onLoss: [
        "...",
      ],
    },
    rewardCrests: 200,
  },

  {
    id: "keldara",
    name: "Kel'dara",
    title: "The Warden of the Deep",
    innerWinsRequired: 15,
    difficulty: "Lord",
    starterDeckId: "starter_h01",
    avatarIcon: "✦",
    accentColor: "#6d28d9",
    lore: "Kel'dara was the first being the Inner Emperium produced on its own — not summoned, not assigned, not wandered in. The Emperium made Kel'dara from what previous Aenarchists left behind: their strategies, their failures, their almost-victories. To fight Kel'dara is to fight everyone who came before you.",
    dialogue: {
      approach: [
        "I know your deck.",
        "I know your tendencies.",
        "I know the move you make when you're afraid.",
        "Show me something new.",
      ],
      onWin: [
        "...",
        "Something new.",
        "Good.",
        "Keep going.",
      ],
      onLoss: [
        "Predictable.",
        "Come back when you've surprised yourself.",
      ],
    },
    rewardCrests: 280,
  },

  {
    id: "the_deep",
    name: "???",
    title: "???",
    innerWinsRequired: 30,
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    avatarIcon: "◆",
    accentColor: "#ff2266",
    lore: "???",
    locked: true,
    dialogue: {
      approach: [ "..." ],
      onWin:    [ "..." ],
      onLoss:   [ "..." ],
    },
    rewardCrests: 500,
  },
];

export const BOOSTER_PACKS = [
  // ═══════════════════════════════════════════════════════════════
  // ◆ CORE ARCHETYPES — The Foundational Five ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 1: Void Eclipse (VE) ────────────────────────────────
  {
    id: "void_eclipse",
    name: "Void Eclipse",
    subtitle: "Phantom Beings · Nihil · Ritual",
    description: "Spectral Tiger, Shadow Fox, Inverted Serpent. All 9 Nihil Tomes and all 9 Ritual Tomes. Brooding, strategic, slow-burn.",
    flavor: "◆ Your shadow already belongs to the dark. ◆",
    icon: "◇",
    code: "VE",
    gradient: "linear-gradient(135deg, #1a0a2e, #0a0015)",
    accentColor: "#7c3aed",
    glowColor: "#7c3aed55",
    wrapperColor: "Purple + Black",
    gold: "VE-039G — Haunting Presence (Spectral Tiger Lord U'hlti)",
    mythic: "VE-033M — X'aol (Evil Affinity — most feared card in the game)",
    cost: 5,
    signatureTier: "holo_void",
  },
  // ── Pack 2: Solar Ascension (SA) ─────────────────────────────
  {
    id: "solar_ascension",
    name: "Solar Ascension",
    subtitle: "Space Beings · Construction · Treasure",
    description: "H-01, H-09, Xulan. All 9 Construction Tomes and all 9 Treasure Tomes. Powerful, aggressive, high-Lyt energy.",
    flavor: "◆ The stars bend to their will. ◆",
    icon: "✦",
    code: "SA",
    gradient: "linear-gradient(135deg, #1e3a5f, #0a1a3a)",
    accentColor: "#3b82f6",
    glowColor: "#3b82f655",
    wrapperColor: "Gold foil",
    gold: "SA-039G — Show Stomper (Premier Dragon Lord U'hlti, dim Anima by 10)",
    mythic: "SA-033M — Space Race (Win condition Prophecy for Space ilk)",
    cost: 5,
    signatureTier: "holo_gold",
  },
  // ── Pack 3: Clockwork Dominion (CD) ──────────────────────────
  {
    id: "clockwork_dominion",
    name: "Clockwork Dominion",
    subtitle: "Time Beings · Omnia · Contract",
    description: "Z4Z, DG-0, Antdroid. All 9 Omnia Tomes and all 9 Contract Tomes. Control-heavy, oppressive, engine-building.",
    flavor: "◆ Time is a cog in the machine's design. ◆",
    icon: "⟳",
    code: "CD",
    gradient: "linear-gradient(135deg, #0a2020, #051010)",
    accentColor: "#0d9488",
    glowColor: "#0d948855",
    wrapperColor: "Teal + Silver foil",
    gold: "CD-039G — Sword & Shield of O'mnis (nullify any card activation)",
    mythic: "CD-033M — Time to Pretend (Win condition for 2 Supreme Time beings)",
    cost: 5,
    signatureTier: "holo_depths",
  },
  // ── Pack 4: Dragon's Wrath (DW) ───────────────────────────────
  {
    id: "dragons_wrath",
    name: "Dragon's Wrath",
    subtitle: "Dragon Beings · Ancient Sorcery · Shadow Gambling",
    description: "Lucky Dragon, Premier Dragon, Chaos Dragon. All Ancient Sorcery and Shadow Gambling Tomes. Chaotic, high-variance, big swings.",
    flavor: "◆ Even fire bows before the Emperium. ◆",
    icon: "◈",
    code: "DW",
    gradient: "linear-gradient(135deg, #3a0a0a, #1a0000)",
    accentColor: "#dc2626",
    glowColor: "#dc262655",
    wrapperColor: "Purple + Red foil",
    gold: "DW-039G — Casino Royale (auto-win your next Shadow Gambling)",
    mythic: "DW-033M — Dragonforce (Win condition for 2 Supreme Dragon beings)",
    cost: 5,
    signatureTier: "holo_solrend",
  },
  // ── Pack 5: Supreme Council (SC) ─────────────────────────────
  {
    id: "supreme_council",
    name: "Supreme Council",
    subtitle: "All U'hltis · Affinities · Prophecies · Curios",
    description: "No beings. Pure game-ending power. All 12 U'hltis, all 4 Affinities, all 8 Prophecies, all 9 Curios. The prestige pack.",
    flavor: "◆ The council has spoken. The rest is ritual. ◆",
    icon: "⬡",
    code: "SC",
    gradient: "linear-gradient(135deg, #2a1a0a, #15080a)",
    accentColor: "#d4a84b",
    glowColor: "#d4a84b55",
    wrapperColor: "Rainbow holographic foil",
    gold: "SC-033G — I or None (force both players to ruin beings until 1 each remains)",
    mythic: "SC-021M — Tablet of O'mnis (Win condition: 2 Supreme Beings + D'/A'eternuz)",
    cost: 5,
    signatureTier: "holo_aeternus",
  },

  // ═══════════════════════════════════════════════════════════════
  // ◆ SUPPORT & UTILITY — The Competitive Edge ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 6: Instinct Rising (IR) ─────────────────────────────
  {
    id: "instinct_rising",
    name: "Instinct Rising",
    subtitle: "All 36 Primeras · Curios",
    description: "No beings. Pure support. All 36 Primeras and 9 Curios. The most important competitive pack — Primeras are guaranteed here.",
    flavor: "◆ Instinct was never lost — it was waiting. ◆",
    icon: "◉",
    code: "IR",
    gradient: "linear-gradient(135deg, #1a2e0a, #0a1a05)",
    accentColor: "#16a34a",
    glowColor: "#16a34a55",
    wrapperColor: "Gold + Green foil",
    gold: "IR-045G — None More Than I (beings over 10 Lyt instantly ruined)",
    mythic: "IR-036M — Bloody Hell (Win condition: any Warlock loses 45+ Anima)",
    cost: 5,
    signatureTier: "holo_shadow",
  },
  // ── Pack 7: Unstable World (UW) ───────────────────────────────
  {
    id: "unstable_world",
    name: "Unstable World",
    subtitle: "All L1 Beings · U'hltis · Shadow Gambling · Affinities",
    description: "Every ilk's L1 beings, all U'hltis, Shadow Gambling, Contract Tomes, all 4 Esclings, all 4 Affinities, 1 Prophecy. The chaos pack.",
    flavor: "◆ In the Unstable World, anything can happen — and does. ◆",
    icon: "✶",
    code: "UW",
    gradient: "linear-gradient(135deg, #1a0a1a, #0a0010)",
    accentColor: "#ff2266",
    glowColor: "#ff226655",
    wrapperColor: "Rainbow holographic foil",
    gold: "UW-050G — Dragon Eyes (roll double-1s to win 2 Shadow Gamblings instantly)",
    mythic: "UW-033M — Ace of Shades (win 7 Shadow Gamblings in a row, win the duel)",
    cost: 5,
    signatureTier: "holo_jynxi",
  },

  // ═══════════════════════════════════════════════════════════════
  // ✦ HOLOGRAPHIC BOUTIQUE — Limited Edition Foil Variants ✦
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 8: Void Eclipse — Mythic Holo Edition (VE-M) ────────
  {
    id: "void_eclipse_mythic",
    name: "Void Eclipse — Mythic Edition",
    subtitle: "Every card pulls as ◆ MYTHIC or ◈ SILVER",
    description: "The full Void Eclipse lineup, guaranteed holographic foil on every single card. Phantom Beings, Nihil, Ritual. Each card shimmers with mythic or silver luminescence.",
    flavor: "◆ From the void, light emerges differently. ◆",
    icon: "◆",
    code: "VE-M",
    gradient: "linear-gradient(135deg, #2a1a3e, #1a0a25)",
    accentColor: "#ff66cc",
    glowColor: "#ff66cc88",
    wrapperColor: "Mythic Shimmer",
    gold: "VE-M-039 — Haunting Presence (Spectral Tiger Lord U'hlti)",
    mythic: "VE-M-033 — X'aol (Evil Affinity)",
    cost: 12,
    holoGuarantee: true,
    holoTiers: ["holo_mythic", "holo_silver"],
  },
  // ── Pack 9: Solar Ascension — Gold Holo Edition (SA-G) ────────
  {
    id: "solar_ascension_gold",
    name: "Solar Ascension — Gold Edition",
    subtitle: "Every card pulls as ✦ GOLD or ◈ SILVER",
    description: "The full Solar Ascension lineup, guaranteed holographic foil. Space Beings, Construction, Treasure. Premium gold shimmer across every pull.",
    flavor: "◆ The golden light of achievement shines eternal. ◆",
    icon: "✦",
    code: "SA-G",
    gradient: "linear-gradient(135deg, #2e3a5f, #1a2a4a)",
    accentColor: "#ffd700",
    glowColor: "#ffd70088",
    wrapperColor: "Gold Foil",
    gold: "SA-G-039 — Show Stomper (Premier Dragon Lord U'hlti)",
    mythic: "SA-G-033 — Space Race (Win condition Prophecy)",
    cost: 12,
    holoGuarantee: true,
    holoTiers: ["holo_gold", "holo_silver"],
  },
  // ── Pack 10: Clockwork Dominion — Depths Holo (CD-D) ────────
  {
    id: "clockwork_dominion_depths",
    name: "Clockwork Dominion — Depths Edition",
    subtitle: "Every card pulls as ◆ DEPTHS cascade foil",
    description: "Time Beings, Omnia, Contract — every card rendered in the signature ocean-blue cascading depths effect. Strategic gameplay meets haunting visuals.",
    flavor: "◆ In the depths of time, all mysteries await. ◆",
    icon: "◆",
    code: "CD-D",
    gradient: "linear-gradient(135deg, #0a3030, #051820)",
    accentColor: "#00aaff",
    glowColor: "#00aaff88",
    wrapperColor: "Depths Cascade",
    gold: "CD-D-039 — Sword & Shield of O'mnis",
    mythic: "CD-D-033 — Time to Pretend",
    cost: 15,
    holoGuarantee: true,
    holoTiers: ["holo_depths"],
  },
  // ── Pack 11: Dragon's Wrath — Solrend Holo (DW-S) ────────────
  {
    id: "dragons_wrath_solrend",
    name: "Dragon's Wrath — Solrend Edition",
    subtitle: "Every card pulls with ◆ SOLREND crimson pulse",
    description: "Dragon Beings, Ancient Sorcery, Shadow Gambling — wrapped in the burning red Solrend foil. Chaos and fire in every pull.",
    flavor: "◆ The Solrend: where fire forgets its mercy. ◆",
    icon: "◆",
    code: "DW-S",
    gradient: "linear-gradient(135deg, #4a1a1a, #2a0a0a)",
    accentColor: "#ff2266",
    glowColor: "#ff226688",
    wrapperColor: "Solrend Pulse",
    gold: "DW-S-039 — Casino Royale",
    mythic: "DW-S-033 — Dragonforce",
    cost: 14,
    holoGuarantee: true,
    holoTiers: ["holo_solrend"],
  },
  // ── Pack 12: Supreme Council — A'eterni Holo (SC-A) ──────────
  {
    id: "supreme_council_aeterni",
    name: "Supreme Council — A'eterni Edition",
    subtitle: "Every card pulls with ✦ A'ETERNI celestial white foil",
    description: "All U'hltis, Affinities, Prophecies, Curios — the full power of the council rendered in transcendent A'eterni white. Ethereal and absolute.",
    flavor: "◆ In the A'eterni light, all truth is revealed. ◆",
    icon: "✦",
    code: "SC-A",
    gradient: "linear-gradient(135deg, #3a2a1a, #2a1a0a)",
    accentColor: "#f0f0ff",
    glowColor: "#f0f0ff66",
    wrapperColor: "A'eterni Glow",
    gold: "SC-A-033 — I or None",
    mythic: "SC-A-021 — Tablet of O'mnis",
    cost: 16,
    holoGuarantee: true,
    holoTiers: ["holo_aeternus"],
  },
  // ── Pack 13: Instinct Rising — Shadow Holo (IR-SH) ──────────
  {
    id: "instinct_rising_shadow",
    name: "Instinct Rising — Shadow Edition",
    subtitle: "Every card pulls with ◇ SHADOW invert foil",
    description: "All 36 Primeras and 9 Curios — the support deck rendered in mysterious Shadow foil. Every card carries hidden depth.",
    flavor: "◆ Instinct whispers from the shadow. ◆",
    icon: "◇",
    code: "IR-SH",
    gradient: "linear-gradient(135deg, #2a1a3a, #1a0a2a)",
    accentColor: "#9966ff",
    glowColor: "#9966ff77",
    wrapperColor: "Shadow Invert",
    gold: "IR-SH-045 — None More Than I",
    mythic: "IR-SH-036 — Bloody Hell",
    cost: 13,
    holoGuarantee: true,
    holoTiers: ["holo_shadow"],
  },
  // ── Pack 14: Void Eclipse — Void Holo (VE-V) ─────────────────
  {
    id: "void_eclipse_void",
    name: "Void Eclipse — Void Edition",
    subtitle: "Every card pulls with ◈ VOID deep purple pulse",
    description: "Phantom Beings, Nihil, Ritual — each card rendered in the mysterious Void foil. Darkness upon darkness, perfectly synergized.",
    flavor: "◆ When void calls to void, all else fades. ◆",
    icon: "◈",
    code: "VE-V",
    gradient: "linear-gradient(135deg, #3a0a5e, #1a0a3e)",
    accentColor: "#9333ea",
    glowColor: "#9333ea88",
    wrapperColor: "Void Pulse",
    gold: "VE-V-039 — Haunting Presence",
    mythic: "VE-V-033 — X'aol",
    cost: 13,
    holoGuarantee: true,
    holoTiers: ["holo_void"],
  },
  // ── Pack 14b: Unstable World — J'ynxi Edition (UW-J) ─────────
  {
    id: "unstable_world_jynxi",
    name: "Unstable World — J'ynxi Edition",
    subtitle: "Every card pulls with ✦ J'YNXI emerald grin",
    description: "The full chaos lineup, every card wrapped in the trickster's green. L1 beings, U'hltis, Shadow Gambling, Affinities — Friend or Faux, all of it foiled.",
    flavor: "◆ The green that grins back. Friend or faux? ◆",
    icon: "✦",
    code: "UW-J",
    gradient: "linear-gradient(135deg, #0a3a1a, #052a12)",
    accentColor: "#2ee66b",
    glowColor: "#2ee66b88",
    wrapperColor: "J'ynxi Emerald",
    gold: "UW-J-050 — Dragon Eyes",
    mythic: "UW-J-033 — Ace of Shades",
    cost: 14,
    holoGuarantee: true,
    holoTiers: ["holo_jynxi"],
    signatureTier: "holo_jynxi",
  },

  // ═══════════════════════════════════════════════════════════════
  // ◉ MONO-CLASS POWER — Dedicated Pursuit ◉
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 15: Space Ascendants (SP) ────────────────────────────
  {
    id: "space_ascendants",
    name: "Space Ascendants",
    subtitle: "H-01, H-09, Xulan · All Space ilk Tomes",
    description: "Focused on Space beings from every lineage. Construction, Treasure, Shadow Gambling. Build the ultimate Space deck.",
    flavor: "◆ In space, they are gods. ◆",
    icon: "★",
    code: "SP",
    gradient: "linear-gradient(135deg, #0a1a4a, #051030)",
    accentColor: "#60a5fa",
    glowColor: "#60a5fa55",
    wrapperColor: "Starfield foil",
    gold: "SP-009G — Cosmic Burst (all Space beings +3 Lyt this turn)",
    mythic: "SP-021M — Supernova Protocol",
    cost: 6,
  },
  // ── Pack 16: Phantom Mastery (PH) ────────────────────────────
  {
    id: "phantom_mastery",
    name: "Phantom Mastery",
    subtitle: "Spectral Tiger, Shadow Fox, Inverted Serpent · Phantom focus",
    description: "Deep dive into Phantom ilk. Nihil synergies, Ritual power, Ancient Sorcery. Master the unseen.",
    flavor: "◆ In the phantom's realm, you are never alone. ◆",
    icon: "◇",
    code: "PH",
    gradient: "linear-gradient(135deg, #1a0a2e, #0a0020)",
    accentColor: "#7c3aed",
    glowColor: "#7c3aed55",
    wrapperColor: "Ethereal shimmer",
    gold: "PH-012G — Phantom Echo (beings ruined cost opponent 5 Anima)",
    mythic: "PH-018M — Spectral Ascension",
    cost: 6,
  },
  // ── Pack 17: Time Engine (TM) ────────────────────────────────
  {
    id: "time_engine",
    name: "Time Engine",
    subtitle: "Z4Z, DG-0, Antdroid · Time control mastery",
    description: "Time beings, Omnia contracts, Shadow Gambling for clockwork precision. Build the perfect engine.",
    flavor: "◆ Time turns at your command. ◆",
    icon: "⟳",
    code: "TM",
    gradient: "linear-gradient(135deg, #0a2a2a, #051515)",
    accentColor: "#14b8a6",
    glowColor: "#14b8a655",
    wrapperColor: "Teal mechanism",
    gold: "TM-008G — Temporal Loop (repeat your last Tome effect)",
    mythic: "TM-015M — Eternity Lock",
    cost: 6,
  },
  // ── Pack 18: Dragon's Armory (DR) ───────────────────────────
  {
    id: "dragons_armory",
    name: "Dragon's Armory",
    subtitle: "Lucky, Premier, Chaos Dragons · Dragon focused",
    description: "All three Dragon lines, Ancient Sorcery, Shadow Gambling. Harness draconic chaos and power.",
    flavor: "◆ Dragons fear no arsenal. ◆",
    icon: "◆",
    code: "DR",
    gradient: "linear-gradient(135deg, #4a1a0a, #2a0a00)",
    accentColor: "#f97316",
    glowColor: "#f9731655",
    wrapperColor: "Dragon scale",
    gold: "DR-011G — Breath of the Emperium (+8 Lyt to 1 Dragon)",
    mythic: "DR-019M — Ancient Flight",
    cost: 6,
  },

  // ═══════════════════════════════════════════════════════════════
  // ◈ TOME MASTERY — Spellcraft Focus ◈
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 19: Shadow Gambler's Fortune (SG) ───────────────────
  {
    id: "shadow_gambler",
    name: "Shadow Gambler's Fortune",
    subtitle: "All 8 Shadow Gambling Tomes · Chance & chaos",
    description: "Every Shadow Gambling Tome. Maximize variance and risk-reward. Roll the dice.",
    flavor: "◆ Fortune favors the bold — and the lucky. ◆",
    icon: "◲",
    code: "SG",
    gradient: "linear-gradient(135deg, #2a1a0a, #1a0a05)",
    accentColor: "#f59e0b",
    glowColor: "#f59e0b55",
    wrapperColor: "Velvet + gold",
    gold: "SG-006G — The House Always Loses (2 Shadow Gamblings auto-win)",
    mythic: "SG-012M — Jackpot Infinite",
    cost: 7,
  },
  // ── Pack 20: Ritual's Ascent (RT) ────────────────────────────
  {
    id: "rituals_ascent",
    name: "Ritual's Ascent",
    subtitle: "All 9 Ritual Tomes · Resurrection engine",
    description: "Seal of Omnis, Monoliths, Mausoleums. Soulcerer's line. Resurrect and reclaim.",
    flavor: "◆ Death yields to ritual. ◆",
    icon: "⚱",
    code: "RT",
    gradient: "linear-gradient(135deg, #1a0a2a, #0a0015)",
    accentColor: "#a855f7",
    glowColor: "#a855f755",
    wrapperColor: "Mortuary purple",
    gold: "RT-007G — Soulcerer's Mausoleum (resurrect 2 beings)",
    mythic: "RT-013M — Eternal Return",
    cost: 7,
  },
  // ── Pack 21: Nihil's Embrace (NL) ───────────────────────────
  {
    id: "nihils_embrace",
    name: "Nihil's Embrace",
    subtitle: "All 9 Nihil Tomes · Void synergy",
    description: "Every Nihil Tome. Seal of Nihil, Monoliths, Mausoleums. Embrace the void.",
    flavor: "◆ In nihil, all debts are paid. ◆",
    icon: "◈",
    code: "NL",
    gradient: "linear-gradient(135deg, #0a0a1a, #050510)",
    accentColor: "#8b5cf6",
    glowColor: "#8b5cf655",
    wrapperColor: "Deep purple",
    gold: "NL-008G — Void Ascension (all beings +2 Lyf this turn)",
    mythic: "NL-014M — Nihil Eternal",
    cost: 7,
  },
  // ── Pack 22: Contract Broker (CB) ───────────────────────────
  {
    id: "contract_broker",
    name: "Contract Broker",
    subtitle: "All 9 Contract Tomes · Social engineering",
    description: "Devil's Advocate through King of the Castle. Negotiate your way to victory.",
    flavor: "◆ A contract is only binding if both sides believe it. ◆",
    icon: "⚖",
    code: "CB",
    gradient: "linear-gradient(135deg, #2a1a0a, #1a0a05)",
    accentColor: "#d97706",
    glowColor: "#d9770655",
    wrapperColor: "Parchment + ink",
    gold: "CB-009G — Airtight Agreement (lock opponent's Chao)",
    mythic: "CB-015M — Unbreakable Bond",
    cost: 7,
  },

  // ═══════════════════════════════════════════════════════════════
  // ✦ PREMIER EXPERIENCES — The Collector's Cabinet ✦
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 23: All-Stars Mythic Bundle (ASM) ──────────────────
  {
    id: "all_stars_mythic",
    name: "All-Stars — Mythic Bundle",
    subtitle: "Every archetype's best · Guaranteed ◆ MYTHIC each",
    description: "One curated card from each archetype (all 12 U'hltis included), guaranteed to pull as MYTHIC holographic. Build your dream deck of legends.",
    flavor: "◆ The greatest collection ever assembled. ◆",
    icon: "◆",
    code: "ASM",
    gradient: "linear-gradient(135deg, #3a2a1a, #2a1a0a)",
    accentColor: "#ff66cc",
    glowColor: "#ff66cc88",
    wrapperColor: "Mythic rainbow",
    gold: "ASM-001 — Your choice mythic",
    mythic: "ASM-001M — Rare pulled as pure mythic",
    cost: 20,
    holoGuarantee: true,
    holoTiers: ["holo_mythic"],
  },
  // ── Pack 24: Depths Sanctuary (DS) ────────────────────────────
  {
    id: "depths_sanctuary",
    name: "Depths Sanctuary",
    subtitle: "Complete Endless Depths replica pack",
    description: "Recreate the Gatekeeper's collection. Every card from every trophy tier, guaranteed ◆ DEPTHS foil. The full ocean cascade experience.",
    flavor: "◆ Dive deep. The Depths remember you. ◆",
    icon: "◆",
    code: "DS",
    gradient: "linear-gradient(135deg, #0a2a3a, #051520)",
    accentColor: "#00aaff",
    glowColor: "#00aaff88",
    wrapperColor: "Ocean cascade",
    gold: "DS-042 — Depths pull",
    mythic: "DS-042D — Depths rare pull",
    cost: 18,
    holoGuarantee: true,
    holoTiers: ["holo_depths"],
  },
  // ── Pack 25: Hybrid Chaos (HC) ───────────────────────────────
  {
    id: "hybrid_chaos",
    name: "Hybrid Chaos",
    subtitle: "Cross-ilk power · Mixed rarity holos",
    description: "Beings from two different ilks per pull + support cards. May the chaos guide you. Mixed holo tiers make each pull unique.",
    flavor: "◆ Where ilks converge, chaos blooms. ◆",
    icon: "✶",
    code: "HC",
    gradient: "linear-gradient(135deg, #2a1a2a, #1a0a1a)",
    accentColor: "#ec4899",
    glowColor: "#ec489955",
    wrapperColor: "Fractured prism",
    gold: "HC-033G — Collision Event (1 being from each ilk)",
    mythic: "HC-027M — Dimensional Breach",
    cost: 9,
  },
  // ── Pack 26: Affinity Ascension (AA) ──────────────────────────
  {
    id: "affinity_ascension",
    name: "Affinity Ascension",
    subtitle: "All 4 Affinities · Supreme alignment",
    description: "Every Affinity (X'aol, Y'tyranuz, J'ynxi, Enlightened). Build your ideological empire.",
    flavor: "◆ Choose your path. One affinity defines all. ◆",
    icon: "◉",
    code: "AA",
    gradient: "linear-gradient(135deg, #1a2a1a, #0a1a0a)",
    accentColor: "#10b981",
    glowColor: "#10b98155",
    wrapperColor: "Harmonic alignment",
    gold: "AA-004G — Enlightened Harmony (all ilks +2 Lyt)",
    mythic: "AA-008M — Supreme Ascension",
    cost: 8,
  },
  // ── Pack 27: Prophecy Oracle (PO) ────────────────────────────
  {
    id: "prophecy_oracle",
    name: "Prophecy Oracle",
    subtitle: "All 8 Prophecies + curios",
    description: "Every win condition card. See all possible futures. Complete your collection of destiny.",
    flavor: "◆ The oracle sees all — which path will you choose? ◆",
    icon: "🔮",
    code: "PO",
    gradient: "linear-gradient(135deg, #2a0a3a, #1a0a2a)",
    accentColor: "#a78bfa",
    glowColor: "#a78bfa55",
    wrapperColor: "Crystalline glow",
    gold: "PO-005G — Vision Quest (draw cards, see your future)",
    mythic: "PO-016M — Infinite Sight",
    cost: 9,
  },

  // ═══════════════════════════════════════════════════════════════
  // ◆ INFINITE VARIETY — The Testing Playground ◆
  // ═══════════════════════════════════════════════════════════════

  // ── Pack 28: Randomizer Supreme (RS) ──────────────────────────
  {
    id: "randomizer_supreme",
    name: "Randomizer Supreme",
    subtitle: "Any 10 cards from entire card pool · Pure chaos",
    description: "Pull 10 completely random cards from every card type. No restrictions. No focus. Pure unpredictable Terror.",
    flavor: "◆ Chaos is the only true order. ◆",
    icon: "✶",
    code: "RS",
    gradient: "linear-gradient(135deg, #1a1a2a, #0a0a1a)",
    accentColor: "#f472b6",
    glowColor: "#f472b655",
    wrapperColor: "Quantum uncertainty",
    gold: "RS-Random — Completely random pull",
    mythic: "RS-Random — Anything can be mythic",
    cost: 4,
  },
];

// ── FEATURED / LIMITED EDITION HOLOS ──────────────────────────
// Rotating limited runs with special holo effects beyond silver/gold/mythic
// Each featured card has: id, cardId, holoEffect, seriesName, seriesCode,
//   description, cost, totalPrinted, available, startDate, endDate
export const FEATURED_HOLOS = [
  {
    id: "feat_xaol_void",
    cardId: "aff_evil",
    holoEffect: "void",
    seriesName: "Void Edition",
    seriesCode: "VOID-001",
    description: "X'aol rendered in deep shifting void — the darkness between stars made manifest.",
    flavor: "◆ Even the void holds its breath. ◆",
    cost: 25,
    totalPrinted: 100,
    available: 100,
    accentColor: "#3b0764",
    glowColor: "#3b076488",
  },
  {
    id: "feat_spectral_solrend",
    cardId: "spectral_tiger_lord",
    holoEffect: "solrend",
    seriesName: "Solrend Edition",
    seriesCode: "SOLR-001",
    description: "The Spectral Tiger Lord bathed in Solrend red — a being that has descended beyond rank.",
    flavor: "◆ There is no top. There is only deeper. ◆",
    cost: 25,
    totalPrinted: 100,
    available: 100,
    accentColor: "#ff2266",
    glowColor: "#ff226688",
  },
];

// ─────────────────────────────────────────────────────────────
// ◆ THE FOIL VAULT — guaranteed single holographics ◆
// ─────────────────────────────────────────────────────────────
// The boutique's centerpiece. No gambling: pick the exact card in the
// exact tier you want and it is yours. Every one of the 8 holo tiers
// has flagship singles here, spread across archetypes so the whole
// rainbow is reachable through deliberate purchase.
//
// Pricing ladder (Divine Crests) by tier — chase tiers cost more:
//   silver 6 · gold 9 · void 11 · shadow 11 · depths 13 · solrend 13
//   aeterni 15 · mythic 18
// Marquee cards (Lords, U'hltis, Affinities, Prophecies) carry a +4 premium.
//
// Each entry: { id, cardId, tier, cost, blurb }
// `tier` is a holo_* string used verbatim as holoTier on purchase.
export const FOIL_VAULT_PRICE = {
  holo_silver: 6, holo_gold: 9, holo_void: 11, holo_shadow: 11,
  holo_jynxi: 12, holo_depths: 13, holo_solrend: 13, holo_aeternus: 15, holo_mythic: 18,
};
export const FOIL_VAULT_PREMIUM = 4; // added for marquee (Lord/U'hlti/Affinity/Prophecy)

// ─────────────────────────────────────────────────────────────
// FOIL VAULT — now GENERATED, not hand-listed.
//
// The vault must contain EVERY card in the game, including cards
// that don't exist yet. So instead of typing one entry per card,
// we walk the master card list (getAllGrimoireCards) and mint a
// vault entry for each one automatically. Ship a new card in
// cards.js → it appears in the vault on next load. Zero upkeep.
//
// Each card gets a SIGNATURE tier (the foil shown by default on
// its tile). The tile's tier selector then lets a player buy that
// same card in ANY of the 9 holo tiers — so "every card in every
// foil" is true without 2,500 static tiles.
//
// Curated cards below keep their hand-written blurb + signature
// tier as OVERRIDES, so the flagships still feel special.
// ─────────────────────────────────────────────────────────────

// Card types that carry the +premium marquee treatment (the chase
// rarities: Lords, U'hltis, Affinities, Prophecies). Detected by
// the card's `type` field so new flagship cards price themselves.
const MARQUEE_TYPES = new Set(["U'hlti", "Affinity", "Prophecy"]);
const isMarqueeCard = (card) => {
  if (!card) return false;
  if (MARQUEE_TYPES.has(card.type)) return true;
  // Lords are Escendants at the top of their line (level 3).
  if (card.type === "Escendant" && card.level === 3) return true;
  return false;
};

// Pick the default foil a card shows on its tile.
//   · curated override wins
//   · marquee cards default to gold (premium presence)
//   · everything else defaults to silver (clean entry tier)
const signatureTierFor = (card, override) => {
  if (override?.tier) return override.tier;
  return isMarqueeCard(card) ? "holo_gold" : "holo_silver";
};

// Hand-curated flavor. cardId → { tier?, blurb?, marquee? }.
// These ONLY override presentation/signature tier — the card is
// always present in the vault regardless of whether it's listed.
export const FOIL_VAULT_OVERRIDES = {
  aff_evil:               { tier: "holo_mythic",   blurb: "X'aol — the most feared card in the game, in full-spectrum mythic foil." },
  premier_dragon_lord:    { tier: "holo_mythic",   blurb: "The First & Only Leader, rendered in rainbow mythic." },
  proph_dragonforce:      { tier: "holo_mythic",   blurb: "Dragonforce win-condition prophecy — mythic chase." },
  h01_lord:               { tier: "holo_gold",     blurb: "H-01 Super Nova in premium gold sheen." },
  u_premier_dragon_lord:  { tier: "holo_gold",     blurb: "Show Stomper U'hlti — gilded." },
  proph_space_race:       { tier: "holo_gold",     blurb: "Space Race win-condition prophecy in gold." },
  h09_lord:               { tier: "holo_silver",   blurb: "H-09 Hyper Nova in cool silver foil." },
  chaos_dragon_lord:      { tier: "holo_solrend",  blurb: "Chaos Dragon Lord — fire that forgets mercy." },
  curio_circle_of_lyf:    { tier: "holo_silver",   blurb: "Circle of Lyf curio, silver-foiled." },
  inverted_serpent_lord:  { tier: "holo_void",     blurb: "Inverted Serpent Lord — void-touched." },
  proph_phantom_planet:   { tier: "holo_void",     blurb: "Phantom Planet prophecy in void foil." },
  shadow_fox_lord:        { tier: "holo_shadow",   blurb: "Shadow Fox Lord in its namesake invert foil." },
  u_shadow_fox_lord:      { tier: "holo_shadow",   blurb: "Shadow Fox U'hlti — shadow-wrapped." },
  proph_bloody_hell:      { tier: "holo_shadow",   blurb: "Bloody Hell win-condition in shadow invert." },
  z4z_lord:               { tier: "holo_depths",   blurb: "Z4Z Lord in the ocean-blue Depths cascade." },
  proph_time_to_pretend:  { tier: "holo_depths",   blurb: "Time to Pretend prophecy — Depths edition." },
  dg0_lord:               { tier: "holo_depths",   blurb: "DG-0 Lord wrapped in cascading depths." },
  spectral_tiger_lord:    { tier: "holo_solrend",  blurb: "Spectral Tiger Lord burning in Solrend crimson." },
  proph_ace_of_shades:    { tier: "holo_solrend",  blurb: "Ace of Shades prophecy in Solrend red." },
  proph_tablet_omnis:     { tier: "holo_aeternus", blurb: "Tablet of O'mnis — the ultimate win-condition, in A'eterni white." },
  xulan_lord:             { tier: "holo_aeternus", blurb: "Xulan Lord ascended in celestial white." },
  aff_awoken:             { tier: "holo_aeternus", blurb: "The Awoken affinity, rendered transcendent." },
  aff_jynxi:              { tier: "holo_jynxi",    blurb: "Friend or Faux — the J'ynxi affinity in its own grinning green." },
  curio_jynxi_wares:      { tier: "holo_jynxi",    blurb: "J'ynxi's Wares curio, wrapped in trickster emerald." },
  lucky_dragon_lord:      { tier: "holo_jynxi",    blurb: "Lucky Dragon Lord — fortune's green smile." },
};

// Build the full vault from the master card list.
let _foilVaultCache = null;
export const buildFoilVault = (allCards) => {
  if (!Array.isArray(allCards)) return [];
  return allCards
    .filter((c) => c && c.id && c.name)
    .map((card) => {
      const override = FOIL_VAULT_OVERRIDES[card.id] || null;
      const marquee = isMarqueeCard(card);
      const tier = signatureTierFor(card, override);
      return {
        id: `fv_${card.id}`,
        cardId: card.id,
        cardName: card.name,
        cardType: card.type,
        tier,                       // signature/default foil for the tile
        marquee,
        curated: !!override,        // flagged so the gallery can spotlight it
        blurb: override?.blurb || null,
      };
    });
};

// The live vault. Generated once from getAllGrimoireCards.
export const getFoilVault = () => {
  if (_foilVaultCache) return _foilVaultCache;
  _foilVaultCache = buildFoilVault(getAllGrimoireCards());
  return _foilVaultCache;
};

// Back-compat: existing imports of FOIL_VAULT keep working. This is
// the generated list, evaluated at module load.
export const FOIL_VAULT = getFoilVault();

// Resolve the price for a vault entry in a GIVEN tier. The tier may
// be the entry's signature tier or any tier the player selected on
// the tile, so price is always computed from (tier + marquee).
export const getFoilVaultCost = (entry, tierOverride) => {
  const tier = tierOverride || entry?.tier;
  const base = FOIL_VAULT_PRICE[tier] ?? 10;
  return base + (entry?.marquee ? FOIL_VAULT_PREMIUM : 0);
};

// ─────────────────────────────────────────────────────────────
// STARTER DECK DESCRIPTIONS (for Story Mode selection)
// ─────────────────────────────────────────────────────────────

export const STARTER_DECK_LORE = {
  starter_h01:              { name: "H-01 Blue Giant",       meta: "Space",   flavor: "Sons of the Suns — words are their first resort." },
  starter_h09:              { name: "H-09 Neutron Star",     meta: "Space",   flavor: "Generals of the Stars — militancy is their first option." },
  starter_xulan:            { name: "Xulan Wanderer",        meta: "Space",   flavor: "Assassins who drift through the Void, seeking revenge." },
  starter_spectral_tiger:   { name: "Spectral Tiger",        meta: "Phantom", flavor: "Spoiled kittens of the Spirit Worlds, now hunting." },
  starter_shadow_fox:       { name: "Shadow Fox",            meta: "Phantom", flavor: "Born at the Bottomless Depths of the darkest Shadow Spheres." },
  starter_inverted_serpent: { name: "Inverted Serpent",      meta: "Phantom", flavor: "Protectors of the Shadow Forest's entrance." },
  starter_z4z:              { name: "Z4Z Demon",             meta: "Time",    flavor: "Responsible for destroying 1% of the Unstable Worlds." },
  starter_dg0:              { name: "DG-0 Angel",            meta: "Time",    flavor: "Through tremendous effort, healed 1% of dying beings." },
  starter_antdroid:         { name: "Antdroid",              meta: "Time",    flavor: "The most common robot Beb — it only seeks Anima." },
  starter_lucky_dragon:     { name: "Lucky Dragon",          meta: "Dragon",  flavor: "Rarely seen, born in the Unstable World with a fondness for Shade." },
  starter_premier_dragon:   { name: "Premier Dragon",        meta: "Dragon",  flavor: "The First & Only Leader — when born, Bloom touches the surrounding area." },
  starter_chaos_dragon:     { name: "Chaos Dragon",          meta: "Dragon",  flavor: "The One & Only Leader — its birth brings Doom and destruction." },
};

// ═══════════════════════════════════════════════════════════════
// ENDLESS DEPTHS — PROCEDURAL OPPONENT NAME POOL
// ═══════════════════════════════════════════════════════════════
// Krush-style names: real words, cryptically spelled.
// Format follows your canonical characters (T'yrent = Tyrant,
// Z'hefir = Zephyr, X'aol = Exile, V'ael = Veil, Rük = Rook/Wreck).
//
// Each entry: { name, hidden } where `hidden` is the real word —
// used internally for naming-pattern reference, never shown in-game.
// Players sound out names and get satisfying click-of-recognition.
// ═══════════════════════════════════════════════════════════════
export const DEPTHS_OPPONENTS = {
  // Low-tier — Escling/Beb opponents (0-800 Elo)
  // Vibes: new, unprepared, hopeful, searching
  escling_beb: [
    { name: "N'vise",   hidden: "Novice"     },
    { name: "W'ndrah",  hidden: "Wanderer"   },
    { name: "S'kher",   hidden: "Seeker"     },
    { name: "T'ndher",  hidden: "Tender"     },
    { name: "H'pful",   hidden: "Hopeful"    },
    { name: "L'sot",    hidden: "Lost"       },
    { name: "Hach'lyn", hidden: "Hatchling"  },
    { name: "P'lgrym",  hidden: "Pilgrim"    },
    { name: "Y'ung",    hidden: "Young"      },
    { name: "Drf'ter",  hidden: "Drifter"    },
    { name: "R'kyi",    hidden: "Rookie"     },
    { name: "N'ubent",  hidden: "New-bent"   },
  ],

  // Mid-tier — Champion/Lord opponents (800-1700 Elo)
  // Vibes: proven, practiced, strategic, committed
  champion_lord: [
    { name: "V'gyl",      hidden: "Vigil"       },
    { name: "K'nyng",     hidden: "Cunning"     },
    { name: "Pr'vyn",     hidden: "Proven"      },
    { name: "V't'ran",    hidden: "Veteran"     },
    { name: "R'vhal",     hidden: "Rival"       },
    { name: "D'wlyst",    hidden: "Duelist"     },
    { name: "A'dept",     hidden: "Adept"       },
    { name: "M'rcur",     hidden: "Mercury"     },
    { name: "S'twart",    hidden: "Stalwart"    },
    { name: "B'nden",     hidden: "Bonded"      },
    { name: "Ōth'keep",   hidden: "Oathkeeper"  },
    { name: "Thresh'ul",  hidden: "Threshold"   },
  ],

  // High-tier — Escendant/Aenarchist opponents (1700-2800 Elo)
  // Vibes: mythic, elemental, prophetic, unstoppable
  escendant_aenarchist: [
    { name: "P'rgon",   hidden: "Paragon"     },
    { name: "T'mpest",  hidden: "Tempest"     },
    { name: "O'rakyl",  hidden: "Oracle"      },
    { name: "Inf'rno",  hidden: "Inferno"     },
    { name: "L'gyn",    hidden: "Legion"      },
    { name: "W'rath",   hidden: "Wrath"       },
    { name: "O'myn",    hidden: "Omen"        },
    { name: "An'shent", hidden: "Ancient"     },
    { name: "Her'uld",  hidden: "Herald"      },
    { name: "C'lossuz", hidden: "Colossus"    },
    { name: "Eld'rych", hidden: "Eldritch"    },
    { name: "Fyr'brand",hidden: "Firebrand"   },
  ],

  // Solrend tier (2800+, no ceiling)
  // Vibes: apex, unmade, nameless, ending
  solrend: [
    { name: "A'pyx",    hidden: "Apex"        },
    { name: "Et'rnul",  hidden: "Eternal"     },
    { name: "P'numbra", hidden: "Penumbra"    },
    { name: "Entrop'",  hidden: "Entropy"     },
    { name: "Ab'yss",   hidden: "Abyss"       },
    { name: "Nihil'ys", hidden: "Nihilist"    },
    { name: "V'dwal",   hidden: "Voidwalker"  },
    { name: "N'amles",  hidden: "Nameless"    },
    { name: "R'kning",  hidden: "Reckoning"   },
    { name: "Unm'de",   hidden: "Unmade"      },
  ],
};

// Helper — pick a name for an opponent given current Elo tier
export const pickDepthsOpponentName = (elo = 0) => {
  let pool;
  if (elo < 800)       pool = DEPTHS_OPPONENTS.escling_beb;
  else if (elo < 1700) pool = DEPTHS_OPPONENTS.champion_lord;
  else if (elo < 2800) pool = DEPTHS_OPPONENTS.escendant_aenarchist;
  else                 pool = DEPTHS_OPPONENTS.solrend;
  // Solrend past 3500 — 30% chance of nameless arrival
  if (elo >= 3500 && Math.random() < 0.3) {
    return { name: "—", hidden: null, nameless: true };
  }
  return pool[Math.floor(Math.random() * pool.length)];
};